import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { addSuppression, removeSuppression, subscribeSuppressions } from '../services/campaigns';
import type { Suppression, SuppressionReason } from '../../functions/src/shared/types';
import { formatTimestamp } from '../lib/format';
import BulkSuppressionImport from '../components/BulkSuppressionImport';
import { isValidEmail } from '../lib/csv';

const REASON_LABELS: Record<SuppressionReason, string> = {
  hard_bounce: 'Hard bounce',
  spam_complaint: 'Spam complaint',
  unsubscribe: 'Unsubscribed',
  manual: 'Added manually',
};

export default function SuppressionsPage() {
  const [suppressions, setSuppressions] = useState<Suppression[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(
    () =>
      subscribeSuppressions(
        (next) => {
          setSuppressions(next);
          setError(null);
        },
        (err) => setError(err.message),
      ),
    [],
  );

  const emailIsValid = isValidEmail(newEmail);

  async function handleAdd() {
    setBusy(true);
    try {
      await addSuppression(newEmail);
      setNewEmail('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add that address');
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(id: string) {
    setBusy(true);
    try {
      await removeSuppression(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove that address');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5" component="h1" gutterBottom>
          Suppression list
        </Typography>
        <Typography variant="body2" color="text.secondary">
          These addresses are skipped by every campaign. Unsubscribes, hard bounces, and spam
          complaints are added automatically — removing someone here does not undo their request
          to stop receiving email.
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
          <TextField
            label="Add an address"
            value={newEmail}
            onChange={(event) => setNewEmail(event.target.value)}
            error={newEmail.length > 0 && !emailIsValid}
            helperText={
              newEmail.length > 0 && !emailIsValid ? 'That is not a valid email address' : ' '
            }
            sx={{ minWidth: 280 }}
          />
          <Button
            variant="contained"
            disabled={!emailIsValid || busy}
            onClick={() => void handleAdd()}
            sx={{ mt: 1 }}
          >
            Add
          </Button>
        </Stack>
      </Paper>

      <BulkSuppressionImport />

      {suppressions === null && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress aria-label="Loading suppression list" />
        </Box>
      )}

      {suppressions?.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1">No suppressed addresses.</Typography>
        </Paper>
      )}

      {suppressions && suppressions.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell component="th" scope="col">Email</TableCell>
                <TableCell component="th" scope="col">Reason</TableCell>
                <TableCell component="th" scope="col">Detail</TableCell>
                <TableCell component="th" scope="col">Added</TableCell>
                <TableCell component="th" scope="col">
                  <span>Actions</span>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {suppressions.map((suppression) => (
                <TableRow key={suppression.id}>
                  <TableCell>{suppression.email}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={REASON_LABELS[suppression.reason] ?? suppression.reason}
                      color={suppression.reason === 'manual' ? 'default' : 'error'}
                    />
                  </TableCell>
                  <TableCell>{suppression.detail || '—'}</TableCell>
                  <TableCell>{formatTimestamp(suppression.createdAt)}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      disabled={busy}
                      onClick={() => void handleRemove(suppression.id)}
                      aria-label={`Remove ${suppression.email} from the suppression list`}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}
